// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { FunctionalComponent, h } from '@stencil/core';

import BonusCode from './BonusCode';
import NoFollowLink from './NoFollowLink';
import { FormattedPromotion } from '../../types';
import showTerms from '../../utils/terms';

interface PromotionCardProps {
    promotion: FormattedPromotion | null;
    logPromotionClick: () => void;
    handleBonusCodeClick?: () => void;
    bonusCodeWasClicked?: boolean;
    promoCodeLabel?: string;
    noCodeLabel?: string;
    claimLabel?: string;
    seeMoreLabel?: string;
    useBoxShadow?: boolean;
    termsAreOwnRow?: boolean;
    openNewTab?: boolean;
    terms?: 'geo' | 'affiliate';
    handleTermsClick?: () => void;
    termsToggle?: boolean;
    positionString?: string;
    showValues?: boolean;
    showReviewLink?: boolean;
}

const PromotionCard: FunctionalComponent<PromotionCardProps> = ({
    promotion,
    logPromotionClick,
    handleBonusCodeClick,
    bonusCodeWasClicked = false,
    promoCodeLabel,
    noCodeLabel,
    claimLabel,
    seeMoreLabel,
    useBoxShadow = false,
    termsAreOwnRow = false,
    openNewTab = false,
    terms = 'geo',
    handleTermsClick,
    termsToggle = false,
    positionString = '',
    showValues = false,
    showReviewLink = false,
}) => {
    const termsText = promotion ? showTerms(promotion, terms) : '';

    let shortTerms = '';
    let showShortTerms = false;
    if (termsText.length > 140) {
        shortTerms = `${termsText.slice(0, 140)}...`;
        showShortTerms = true;
    }

    const propMarfeelConversion = promotion?.link && promotion?.link.includes('switchboard') ? 'switchboard-click' : '';

    const claimPrefixRegex = /^claim\b\s*/i;

    function localizeClaimPrefix(text: string, label: string): string {
        if (!text || text === '-') {
            return text;
        }

        const match = text.match(claimPrefixRegex);
        if (!match) {
            return text;
        }

        const remainder = text.slice(match[0].length).trimStart();
        return remainder ? `${label} ${remainder}` : label;
    }

    const handleThisTermsClick = () => {
        if (handleTermsClick) {
            handleTermsClick();
        }
    };

    const rawButtonText = promotion?.buttonText || '-';
    const buttonText = claimLabel ? localizeClaimPrefix(rawButtonText, claimLabel) : rawButtonText;

    return (
        <div class={useBoxShadow ? `item item__shadow font` : `item font`}>
            <div class="item__content">
                {positionString !== '' && <div class="item__position-circle">{positionString}</div>}
                {promotion ? (
                    <lazy-load-logo
                        class="item__logo"
                        logo={promotion.logo}
                        promoId={promotion.id}
                        logoColor={promotion.logoColor}
                    />
                ) : (
                    <div class="skeleton-item__logo"></div>
                )}
                <div class={`${promotion ? '' : 'skeleton-'}item__description`}>
                    <NoFollowLink
                        onClick={logPromotionClick}
                        href={promotion?.link}
                        target={openNewTab ? '_blank' : ''}
                        style={{ textDecoration: 'none' }}
                    >
                        <div class="item__book-name">{promotion?.bookName}</div>
                        <div class="item__cta">{promotion?.cta}</div>
                    </NoFollowLink>
                    {!termsAreOwnRow && !termsToggle && showShortTerms && (
                        <div class="item__terms">
                            {shortTerms}
                            <span class="item__terms-seemore" onClick={handleThisTermsClick}>
                                {seeMoreLabel || 'See More'}
                            </span>
                        </div>
                    )}
                    {!termsAreOwnRow && (termsToggle || !showShortTerms) && <div class="item__terms">{termsText}</div>}
                </div>
                {showValues && (
                    <div class="item__values">
                        {promotion?.valueProps && (
                            <ul class="item__value-props">
                                {promotion.valueProps.map((valueProp, index) => (
                                    <li key={index}>{valueProp}</li>
                                ))}
                            </ul>
                        )}
                        {showReviewLink && promotion?.reviewLink && (
                            <div class="item__review-link">
                                <NoFollowLink
                                    onClick={logPromotionClick}
                                    href={promotion.reviewLink}
                                    target={openNewTab ? '_blank' : ''}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <div class="item__review-link-text">Read Review</div>
                                </NoFollowLink>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div class={showReviewLink ? 'buttons buttons-condensed' : 'buttons'}>
                {handleBonusCodeClick && (
                    <div class="button__half">
                        <BonusCode
                            bonusCode={promotion?.bonusCode}
                            bonusCodeWasClicked={bonusCodeWasClicked}
                            handleClick={handleBonusCodeClick}
                            labelText={promoCodeLabel}
                            noCodeLabel={noCodeLabel}
                        />
                    </div>
                )}
                <div class="button button__half">
                    <NoFollowLink
                        onClick={logPromotionClick}
                        href={promotion?.link}
                        target={openNewTab ? '_blank' : ''}
                        style={{ textDecoration: 'none' }}
                    >
                        <div class="button-font" data-mrf-conversion={propMarfeelConversion}>
                            {buttonText}
                        </div>
                    </NoFollowLink>
                </div>
            </div>
            {termsAreOwnRow && <div class="item__terms">{promotion?.termsText}</div>}
        </div>
    );
};

export default PromotionCard;
